/**
 * The one way the web app talks to the API. Adds the signed-in person's token, turns a non-2xx
 * answer into an ApiError carrying the API's own message and per-field messages, and never
 * swallows one: a screen either shows the data or says what went wrong.
 */
import type { ApiErrorBody } from "@tlcrm/shared";

import { config } from "../config";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let tokenSource: () => Promise<string | null> = async () => null;
let onUnauthorized: () => void = () => undefined;

/** The auth provider registers how to get a token and what to do when it stops working. */
export function configureAuth(
  source: () => Promise<string | null>,
  unauthorized: () => void,
): void {
  tokenSource = source;
  onUnauthorized = unauthorized;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; query?: Query; signal?: AbortSignal } = {},
): Promise<T> {
  const url = new URL(`${config.apiBaseUrl}/v1${path}`);
  for (const [key, value] of Object.entries(init.query ?? {})) {
    if (value !== undefined && value !== null && value !== "")
      url.searchParams.set(key, String(value));
  }
  const token = await tokenSource();
  let res: Response;
  try {
    res = await fetch(url, {
      method: init.method ?? "GET",
      headers: {
        ...(token && { authorization: `Bearer ${token}` }),
        ...(init.body !== undefined && { "content-type": "application/json" }),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: init.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, "network", "Can't reach the CRM. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as (ApiErrorBody & T) | null;
  if (!res.ok) {
    if (res.status === 401) onUnauthorized();
    const e = body?.error;
    throw new ApiError(
      res.status,
      e?.code ?? "unknown",
      e?.message ?? `The CRM answered ${res.status}.`,
      e?.fields,
      e?.details,
    );
  }
  return body as T;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Try again.";
}
